import {
  createSlice,
  createAction,
  createSelector,
  PayloadAction,
  Selector,
  ActionCreatorWithPayload,
} from '@reduxjs/toolkit';
import { AdminBulkTransferUser, TransactionType } from '@repo/common-types';
import { AppState } from '../root';

export interface BulkTransferState {
  users: AdminBulkTransferUser[];
  error: string | boolean | null;
  loading: boolean;
  transactionType: TransactionType;
}

const initialState: BulkTransferState = {
  users: [
    {
      userId: null,
      alias: '',
      amount: 0,
      error: null,
      transactionId: null,
    },
  ],
  error: null,
  loading: false,
  transactionType: TransactionType.USDT_TRANSFER,
};

const slice = createSlice({
  name: 'bulkTransfer',
  initialState,
  reducers: {
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUsers: (state, action: PayloadAction<AdminBulkTransferUser[]>) => {
      state.users = action.payload;
    },
    removeUser: (state, action: PayloadAction<number>) => {
      state.users.splice(action.payload, 1);
    },
    addUser: (state) => {
      state.users.push({
        userId: null,
        alias: '',
        amount: 0,
        error: null,
        transactionId: null,
      });
    },
    updateUser: (state, action: PayloadAction<{ index: number; user: AdminBulkTransferUser }>) => {
      if (state.users[action.payload.index]) {
        state.users[action.payload.index] = action.payload.user;
      }
    },
    handleBulkTransferResponse: (
      state,
      action: PayloadAction<{ users: AdminBulkTransferUser[]; error: string | boolean | null }>,
    ) => {
      state.error = action.payload.error;
      state.users = action.payload.users;
    },
    resetBulkTransfer: (state: BulkTransferState) => {
      state.users = [];
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.transactionType = initialState.transactionType;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTransactionType: (state, action: PayloadAction<TransactionType>) => {
      state.transactionType = action.payload;
    },
  },
});

export const selectBulkTransferState: Selector<AppState, BulkTransferState> = createSelector(
  [(state: AppState) => state.bulkTransfer],
  (bulkTransfer) => bulkTransfer,
);

export const selectBulkTransferUsers: Selector<AppState, AdminBulkTransferUser[]> = createSelector(
  [selectBulkTransferState],
  (bulkTransfer) => bulkTransfer.users,
);

export const selectBulkTransferTransactionType: Selector<AppState, TransactionType> = createSelector(
  [selectBulkTransferState],
  (bulkTransfer) => bulkTransfer.transactionType,
);

export const selectTotalBulkTransferAmount: Selector<AppState, number> = createSelector(
  [selectBulkTransferState],
  (bulkTransfer) => bulkTransfer.users.reduce((acc, user) => acc + user.amount, 0),
);

export const selectUserTransfer: Selector<AppState, AdminBulkTransferUser> = createSelector(
  [selectBulkTransferState, (state: AppState, index: number) => index],
  (bulkTransfer, index) => bulkTransfer.users[index],
);

export const selectBulkTransferError: Selector<AppState, string | boolean | null> = createSelector(
  [selectBulkTransferState],
  (bulkTransfer) => bulkTransfer.error,
);

export const addUserBulkTransfer: ActionCreatorWithPayload<void> = createAction<void>(
  'bulkTransfer/addUserBulkTransfer',
);
export const bulkAdminTransfer = createAction<{ users: AdminBulkTransferUser[]; transactionType: TransactionType }>(
  'bulkTransfer/bulkAdminTransfer',
);

export const {
  setUsers,
  setError,
  removeUser,
  addUser,
  updateUser,
  resetBulkTransfer,
  setLoading,
  handleBulkTransferResponse,
  setTransactionType,
} = slice.actions;

export const bulkTransferReducer = slice.reducer;
